import { useMemo, useState } from 'react';

import {
    Button,
    Card,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import { createFileRoute, redirect } from '@tanstack/react-router';

import LogoutButton from 'src/components/LogoutButton';
import MainNav from 'src/components/MainNav';
import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import { TeamMemberStatuses } from 'src/constants/TeamRoles';
import { useSnackbar } from 'src/contexts/SnackbarContext';
import useEnvironment from 'src/hooks/useEnvironment';
import useResponsive from 'src/hooks/useResponsive';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import {
    useDeleteTeamMemberMutation,
    useDeleteVisitScheduleMutation,
    useGetVisitSchedulesQuery,
    useUpdatePersonDetailsMutation,
    useUpdateTeamMemberMutation,
} from 'src/store/sageHomeApi';
import { getTeamMemberDisplayName } from 'src/utils/nameUtils';

export const Route = createFileRoute('/(app)/dev/reset-onboarding')({
    beforeLoad: () => {
        const env = import.meta.env.VITE_ENV;
        if (env === 'production') {
            throw redirect({ to: '/home' });
        }
    },
    component: ResetOnboardingComponent,
});

function ResetOnboardingComponent() {
    const { isProd } = useEnvironment();
    const { isMobile } = useResponsive();

    const { showSuccess, showError } = useSnackbar();
    const {
        currentTeam,
        careRecipient,
        isLoading: isLoadingMembers,
        teamMemberByRidMap,
    } = useTeamMemberData();

    const [updatePersonDetails, { isLoading: isUpdatingPerson }] = useUpdatePersonDetailsMutation();
    const [updateTeamMember, { isLoading: isUpdatingTeamMember }] = useUpdateTeamMemberMutation();
    const [deleteVisitSchedule, { isLoading: isDeletingVisitSchedule }] =
        useDeleteVisitScheduleMutation();
    const [deleteTeamMember, { isLoading: isDeletingTeamMember }] = useDeleteTeamMemberMutation();

    const [selectedTeamMemberRid, setSelectedTeamMemberRid] = useState<string>('');
    const [isResetting, setIsResetting] = useState(false);
    const [isDeletingSchedules, setIsDeletingSchedules] = useState(false);
    const [isDeletingMemberships, setIsDeletingMemberships] = useState(false);

    // Fetch visit schedules for the care recipient
    const {
        data: visitSchedules = [],
        isLoading: isLoadingVisitSchedules,
        refetch: refetchVisitSchedules,
    } = useGetVisitSchedulesQuery(
        { careRecipientRid: careRecipient?.teamMemberRid || '' },
        { skip: !careRecipient?.teamMemberRid }
    );

    const allMembers = useMemo(() => currentTeam?.members ?? [], [currentTeam]);

    // Filter members with INVITED status
    const invitedMembers = useMemo(() => {
        return allMembers.filter(
            (member) => member.teamMemberStatus === TeamMemberStatuses.Invited
        );
    }, [allMembers]);

    const selectedTeamMember = teamMemberByRidMap.get(selectedTeamMemberRid);

    const isLoading =
        isLoadingMembers ||
        isLoadingVisitSchedules ||
        isUpdatingPerson ||
        isUpdatingTeamMember ||
        isDeletingVisitSchedule ||
        isDeletingTeamMember;

    const handleResetUser = async () => {
        if (!selectedTeamMember) {
            showError('Please select a user to reset.');
            return;
        }

        if (!selectedTeamMember.teamMemberRid) {
            showError('Selected user does not have a team member RID.');
            return;
        }

        setIsResetting(true);

        try {
            // Reset person details (isTermsAccepted = false)
            await updatePersonDetails({
                personRid: selectedTeamMember.personRid,
                updatePersonRequest: { isTermsAccepted: false },
            }).unwrap();

            // Reset team member (notification settings and status)
            // Note: Attempting to set status to INVITED - API may not support this directly
            await updateTeamMember({
                teamMemberRid: selectedTeamMember.teamMemberRid,
                updateTeamMemberRequest: {
                    isAdmin: selectedTeamMember.isTeamAdmin ?? false,
                    smsNotificationsEnabled: false,
                    emailNotificationsEnabled: false,
                    teamMemberStatus: TeamMemberStatuses.Invited,
                } as {
                    isAdmin: boolean;
                    smsNotificationsEnabled: boolean | undefined;
                    emailNotificationsEnabled: boolean | undefined;
                },
            }).unwrap();

            showSuccess(
                `Successfully reset onboarding for ${getTeamMemberDisplayName(selectedTeamMember)}.`
            );

            setSelectedTeamMemberRid('');
        } catch (error) {
            console.error('Failed to reset onboarding:', error);
            showError(
                `Failed to reset onboarding for ${getTeamMemberDisplayName(selectedTeamMember)}.`
            );
        } finally {
            setIsResetting(false);
        }
    };

    const handleDeleteAllVisitSchedules = async () => {
        if (!careRecipient?.teamMemberRid) {
            showError('Care recipient not found.');
            return;
        }

        if (visitSchedules.length === 0) {
            showError('No visit schedules found to delete.');
            return;
        }

        setIsDeletingSchedules(true);

        try {
            // Delete all visit schedules
            await Promise.all(
                visitSchedules.map((schedule) =>
                    deleteVisitSchedule({
                        careRecipientRid: careRecipient.teamMemberRid,
                        visitScheduleRid: schedule.visitScheduleRid,
                    }).unwrap()
                )
            );

            showSuccess(`Successfully deleted ${visitSchedules.length} visit schedule(s).`);
            // Refetch to update the list
            refetchVisitSchedules();
        } catch (error) {
            console.error('Failed to delete visit schedules:', error);
            showError('Failed to delete visit schedules. Please try again.');
        } finally {
            setIsDeletingSchedules(false);
        }
    };

    const handleDeleteAllInvitedTeamMembers = async () => {
        if (invitedMembers.length === 0) {
            showError('No invited members found to delete.');
            return;
        }

        setIsDeletingMemberships(true);

        try {
            // Delete all invited team memberships
            await Promise.all(
                invitedMembers.map((member) =>
                    deleteTeamMember({ teamMemberRid: member.teamMemberRid }).unwrap()
                )
            );

            showSuccess(
                `Successfully deleted ${invitedMembers.length} invited team membership(s).`
            );
        } catch (error) {
            console.error('Failed to delete team memberships:', error);
            showError('Failed to delete team memberships. Please try again.');
        } finally {
            setIsDeletingMemberships(false);
        }
    };

    // Double-check in component (in case beforeLoad is bypassed)
    if (isProd) {
        return (
            <InnerMaxWidthContainer>
                <Typography variant="h6" color="error">
                    This page is only available in non-production environments.
                </Typography>
            </InnerMaxWidthContainer>
        );
    }

    return (
        <InnerMaxWidthContainer>
            <PageHeader pageTitle="Reset Onboarding" />
            <Stack sx={{ gap: 3, mt: 3 }}>
                <Button
                    variant="text"
                    color="primary"
                    onClick={() => {
                        showSuccess('This is a test toast.', 50000);
                    }}
                >
                    Trigger Toast
                </Button>
                <Card variant="outlined" sx={{ p: 3 }}>
                    <Stack sx={{ gap: 3 }}>
                        <Stack>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                Reset onboarding fields for any user:
                            </Typography>
                            <Typography variant="body2" component="ul">
                                <li>Set their terms acceptance to false</li>
                                <li>Set their SMS and Email notification settings to false</li>
                            </Typography>
                        </Stack>

                        <FormControl fullWidth>
                            <InputLabel id="user-select-label">Select User</InputLabel>
                            <Select
                                labelId="user-select-label"
                                label="Select User"
                                value={selectedTeamMemberRid}
                                onChange={(e) => setSelectedTeamMemberRid(e.target.value)}
                                disabled={isLoadingMembers}
                            >
                                {allMembers.length === 0 ? (
                                    <MenuItem value="" disabled>
                                        {isLoadingMembers ? 'Loading users...' : 'No users found'}
                                    </MenuItem>
                                ) : (
                                    allMembers.map((member) => (
                                        <MenuItem
                                            key={member.teamMemberRid}
                                            value={member.teamMemberRid}
                                        >
                                            {getTeamMemberDisplayName(member)} ({member.email})
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>

                        {selectedTeamMember && (
                            <Stack sx={{ gap: 2.5 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    User Details
                                </Typography>
                                <Stack sx={{ gap: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Name:</strong>{' '}
                                        {getTeamMemberDisplayName(selectedTeamMember)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Email:</strong> {selectedTeamMember.email}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Phone:</strong> {selectedTeamMember.phoneNumber}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Role:</strong> {selectedTeamMember.role || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>isTeamAdmin:</strong>{' '}
                                        {selectedTeamMember.isTeamAdmin ? 'True' : 'False'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Terms Accepted:</strong>{' '}
                                        {selectedTeamMember.isTermsAccepted ? 'True' : 'False'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>SMS Notifications:</strong>{' '}
                                        {selectedTeamMember.notificationSettings
                                            ?.smsNotificationsEnabled
                                            ? 'Enabled'
                                            : 'Disabled'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Email Notifications:</strong>{' '}
                                        {selectedTeamMember.notificationSettings
                                            ?.emailNotificationsEnabled
                                            ? 'Enabled'
                                            : 'Disabled'}
                                    </Typography>
                                </Stack>
                                {selectedTeamMember && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleResetUser}
                                        disabled={isLoading || isResetting}
                                    >
                                        {isResetting ? 'Resetting...' : 'Reset User'}
                                    </Button>
                                )}
                            </Stack>
                        )}
                    </Stack>
                </Card>
                <Card variant="outlined" sx={{ p: 3 }}>
                    <Stack sx={{ gap: 3 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            Delete all ({visitSchedules.length}) Visit Schedules
                        </Typography>
                        <Typography variant="body2">
                            This will delete all visit schedules but past visits will remain.
                        </Typography>
                        {careRecipient ? (
                            <>
                                <Typography variant="body2">
                                    Care Recipient:{' '}
                                    {getTeamMemberDisplayName(careRecipient, {
                                        usePreferred: true,
                                    })}
                                </Typography>

                                {visitSchedules.length > 0 ? (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={handleDeleteAllVisitSchedules}
                                        disabled={isLoading || isDeletingSchedules}
                                    >
                                        {isDeletingSchedules
                                            ? 'Deleting...'
                                            : `Delete ${visitSchedules.length} Visit Schedule${
                                                  visitSchedules.length === 1 ? '' : 's'
                                              }`}
                                    </Button>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No visit schedules found.
                                    </Typography>
                                )}
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No care recipient found.
                            </Typography>
                        )}
                    </Stack>
                </Card>
                <Card variant="outlined" sx={{ p: 3 }}>
                    <Stack sx={{ gap: 3 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            Delete all ({invitedMembers.length}) Invited Team Memberships
                        </Typography>
                        <Typography variant="body2">
                            This will permanently remove all team members with "INVITED" status from
                            the team.
                        </Typography>
                        {invitedMembers.length > 0 ? (
                            <>
                                <Stack sx={{ gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Affected Users:
                                    </Typography>
                                    {invitedMembers.map((member) => (
                                        <Typography
                                            key={member.teamMemberRid}
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            • {getTeamMemberDisplayName(member)} (
                                            {member.phoneNumber})
                                        </Typography>
                                    ))}
                                </Stack>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={handleDeleteAllInvitedTeamMembers}
                                    disabled={isLoading || isDeletingMemberships}
                                >
                                    {isDeletingMemberships
                                        ? 'Deleting...'
                                        : `Delete ${invitedMembers.length} Invited Membership${
                                              invitedMembers.length === 1 ? '' : 's'
                                          }`}
                                </Button>
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No invited members found.
                            </Typography>
                        )}
                    </Stack>
                </Card>
            </Stack>
            <PageFooter includeMainNav={isMobile}>
                <LogoutButton />
            </PageFooter>
            {isMobile && <MainNav />}
        </InnerMaxWidthContainer>
    );
}
